import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useSafeNavigate } from "../../hooks/useSafeNavigate";
import { useSidebar } from "../../contexts/SidebarContext";
import { hapticSelection } from "../../lib/haptics";
import { APP_SHELL_ROUTE_PATHS } from "../../routes/appShellRoutePaths";
import InboxDialog from "./InboxDialog";
import { useOpenNotification } from "./hooks/useOpenNotification";
import { NotificationItem, NotificationOpenSource } from "./types";

interface NotificationsContextValue {
  /** Open the inbox: a centred dialog on pointer devices, a bottom sheet on touch. */
  openInbox: () => void;
  /** Mark read, resolve the destination server-side, navigate. The only way anything is opened. */
  openNotification: (
    id: string,
    source: NotificationOpenSource,
    options?: { replace?: boolean },
  ) => void;
}

const NotificationsContext = createContext<NotificationsContextValue | undefined>(undefined);

export const useNotificationsContext = (): NotificationsContextValue => {
  const context = useContext(NotificationsContext);
  if (context === undefined) {
    throw new Error("useNotificationsContext must be used within a NotificationsProvider");
  }
  return context;
};

interface NotificationsProviderProps {
  children: React.ReactNode;
}

/**
 * Owns the one inbox dialog and the one useOpenNotification for the whole app
 * shell. Mounted in AppShell rather than beside each entry point because both
 * must OUTLIVE the things that trigger them: the profile menu lives in a drawer
 * that unmounts when it closes, the /inbox route unmounts the moment it has
 * opened the dialog, and useSafeNavigate's watchdog reads a ref only a
 * still-mounted owner keeps current — an owner that vanished on success looks
 * exactly like a dead router and earns a full page reload.
 */
export const NotificationsProvider = ({ children }: NotificationsProviderProps) => {
  const navigate = useSafeNavigate();
  const { closeMobileDrawer } = useSidebar();
  const { mutate: open } = useOpenNotification();
  const [inboxOpen, setInboxOpen] = useState(false);

  const openInbox = useCallback(() => setInboxOpen(true), []);
  const closeInbox = useCallback(() => setInboxOpen(false), []);

  const openNotification = useCallback<NotificationsContextValue["openNotification"]>(
    (id, source, options) => {
      open({ id, source, replace: options?.replace });
    },
    [open],
  );

  const handleOpenItem = (item: NotificationItem) => {
    hapticSelection();
    closeInbox();
    closeMobileDrawer();
    openNotification(item.id, "inbox");
  };

  const handleManageAlerts = () => {
    hapticSelection();
    closeInbox();
    closeMobileDrawer();
    navigate(APP_SHELL_ROUTE_PATHS.manageAlerts);
  };

  const value = useMemo(() => ({ openInbox, openNotification }), [openInbox, openNotification]);

  return (
    <NotificationsContext.Provider value={value}>
      {children}
      <InboxDialog
        open={inboxOpen}
        onClose={closeInbox}
        onOpenItem={handleOpenItem}
        onManageAlerts={handleManageAlerts}
      />
    </NotificationsContext.Provider>
  );
};
